import React, { useCallback, useEffect } from 'react';
import { setDato, setPaises, setDepartamentos, setProvincias, setUbigeos } from "../redux/ubigeoSearchSlice";
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Typography, TextField, Autocomplete, Button, Dialog, DialogActions
    , DialogContent, DialogTitle, Select, InputLabel, FormControl, Divider } from '@mui/material';
import { PaperComponentDraggable } from "./PaperComponentDraggable";
import * as ubigeoService from "../services/ubigeoService";
import UbigeoAutocomplete from './common/UbigeoAutocomplete';

const UbigeoSearch = (props) => {
    // Dentro de renderOption el nombre "props" lo usa el propio Autocomplete, asi
    // que se guardan aparte los metodos de la ventana para poder llamarlos.
    const { setSelection, openClose } = props;
    const searchUbigeo = useSelector((state) => state.ubigeoSearch.searchUbigeo);
    const paises = useSelector((state) => state.ubigeoSearch.paises);
    const departamentos = useSelector((state) => state.ubigeoSearch.departamentos);
    const provincias = useSelector((state) => state.ubigeoSearch.provincias);
    const ubigeos = useSelector((state) => state.ubigeoSearch.ubigeos);
    const dispatch = useDispatch();

    const fetchPaises = useCallback(async () => {
        const response = await ubigeoService.getsPais();
        dispatch(setPaises(response));
    }, [dispatch]);

    const fetchDepartamentos = useCallback(async (idPais) => {
        const response = await ubigeoService.getsDepartamentosByIdPais(idPais);
        dispatch(setDepartamentos(response));
    }, [dispatch]);

    const fetchProvincias = useCallback(async (idDepartamento) => {
        const response = await ubigeoService.getsProvinciasByIdDepartamento(idDepartamento);
        dispatch(setProvincias(response));
    }, [dispatch]);

    const fetchUbigeos = useCallback(async (idProvincia) => {
        const response = await ubigeoService.getsUbigeosByIdProvincia(idProvincia);
        dispatch(setUbigeos(response));
    }, [dispatch]);

    useEffect(() => {
        if (props.open) {
            fetchPaises();
        }
    }, [props.open, fetchPaises]);

    return (
        <Dialog
            open={props.open}
            onClose={props.openClose}
            fullWidth={true}
            maxWidth={"md"}
            PaperComponent={PaperComponentDraggable}
        >
            <DialogTitle
                id="draggable-dialog-title"
                style={{ cursor: "move" }}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Buscar Ubigeo
                </Typography>
            </DialogTitle>
            <DialogContent style={{ minHeight: "160px", maxHeight: "420px", padding: "16px 24px" }} >
                <Grid container spacing={2} padding={1}>
                    {/* Busqueda rapida unificada */}
                    <Grid item xs={12}>
                        <UbigeoAutocomplete
                            label="Búsqueda Rápida (Distrito, Provincia o Código)"
                            placeholder="Escriba el nombre del distrito o código de ubigeo..."
                            onChange={(option) => {
                                if (option) {
                                    dispatch(setDato({ name: "idUbigeo", value: option.id }));
                                    dispatch(setDato({ name: "descripcion", value: option.descripcion }));
                                    dispatch(setDato({ name: "departamento", value: option.departamento }));
                                    dispatch(setDato({ name: "provincia", value: option.provincia }));
                                    if (setSelection) {
                                        setSelection(option);
                                        openClose();
                                    }
                                }
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">O seleccionar por jerarquía</Typography>
                        </Divider>
                    </Grid>

                    <Grid item xs={12} sm={3}>
                        <FormControl required fullWidth size="small">
                            <InputLabel htmlFor="input-pais" shrink>Pais</InputLabel>
                            <Select
                                native
                                label="Pais"
                                value={searchUbigeo.idPais}
                                onChange={(event) => {
                                    dispatch(setDepartamentos([]));
                                    dispatch(setProvincias([]));
                                    dispatch(setUbigeos([]));
                                    dispatch(setDato({name: "departamento", value: ""}));
                                    dispatch(setDato({name: "provincia", value: ""}));
                                    dispatch(setDato({name: "descripcion", value: ""}));
                                    dispatch(setDato({name: "idPais", value: event.target.value}));

                                    if (event.target.value != null) {
                                        fetchDepartamentos(event.target.value);
                                    }
                                }}
                                inputProps={{
                                    name: 'idPais',
                                    id: 'input-pais',
                                }}
                                size="small"
                            >
                                <option key={0} value={0}>{"(Seleccione)"}</option>
                                {paises.map((row) => (
                                    <option key={row.id} value={row.id}>{row.descripcion}</option>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Autocomplete
                            name="idDepartamento"
                            value={searchUbigeo.idDepartamento == null ? 0 : searchUbigeo.idDepartamento}
                            inputValue={searchUbigeo.departamento == null ? "" : searchUbigeo.departamento}
                            onInputChange={(event, newValue) => {
                                if (event != null) {
                                    if (event.type === "change") {
                                        dispatch(setDato({name: "departamento", value: newValue == null ? "" : newValue}));
                                    }
                                }
                            }}
                            onChange={(event, newValue) => {
                                dispatch(setProvincias([]));
                                dispatch(setUbigeos([]));
                                dispatch(setDato({name: "provincia", value: ""}));
                                dispatch(setDato({name: "descripcion", value: ""}));
                                dispatch(setDato({name: "idDepartamento", value: newValue == null ? 0 : newValue.id}));
                                dispatch(setDato({name: "departamento", value: newValue == null ? "" : newValue.descripcion}));

                                if (newValue != null)
                                    fetchProvincias(newValue.id);
                            }}
                            options={departamentos}
                            autoHighlight
                            getOptionLabel={(option) => option.descripcion == null ? "" : option.descripcion}
                            isOptionEqualToValue={(option, value) => option.id === (value?.id ?? value)}
                            renderOption={(props, option) => {
                                return (
                                    <li {...props} key={option.id}>
                                    {option.descripcion}
                                    </li>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Departamento"
                                    variant="outlined"
                                    inputProps={{
                                        ...params.inputProps,
                                        autoComplete: 'new-password',
                                    }}
                                />
                            )}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Autocomplete
                            name="idProvincia"
                            value={searchUbigeo.idProvincia == null ? 0 : searchUbigeo.idProvincia}
                            inputValue={searchUbigeo.provincia == null ? "" : searchUbigeo.provincia}
                            onInputChange={(event, newValue) => {
                                if (event != null) {
                                    if (event.type === "change") {
                                        dispatch(setDato({name: "provincia", value: newValue == null ? "" : newValue}));
                                    }
                                }
                            }}
                            onChange={(event, newValue) => {
                                dispatch(setUbigeos([]));
                                dispatch(setDato({name: "descripcion", value: ""}));
                                dispatch(setDato({name: "idProvincia", value: newValue == null ? 0 : newValue.id}));
                                dispatch(setDato({name: "provincia", value: newValue == null ? "" : newValue.descripcion}));

                                if (newValue != null)
                                    fetchUbigeos(newValue.id);
                            }}
                            options={provincias}
                            autoHighlight
                            getOptionLabel={(option) => option.descripcion == null ? "" : option.descripcion}
                            isOptionEqualToValue={(option, value) => option.id === (value?.id ?? value)}
                            renderOption={(props, option) => {
                                return (
                                    <li {...props} key={option.id}>
                                    {option.descripcion}
                                    </li>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Provincia"
                                    variant="outlined"
                                    inputProps={{
                                        ...params.inputProps,
                                        autoComplete: 'new-password',
                                    }}
                                />
                            )}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Autocomplete
                            name="idUbigeo"
                            value={searchUbigeo.idUbigeo == null ? 0 : searchUbigeo.idUbigeo}
                            inputValue={searchUbigeo.descripcion == null ? "" : searchUbigeo.descripcion}
                            onInputChange={(event, newValue) => {
                                if (event != null) {
                                    if (event.type === "change") {
                                        dispatch(setDato({name: "descripcion", value: newValue == null ? "" : newValue}));
                                    }
                                }
                            }}
                            onChange={(event, newValue) => {
                                dispatch(setDato({name: "idUbigeo", value: newValue == null ? 0 : newValue.id}));
                                dispatch(setDato({name: "descripcion", value: newValue == null ? "" : newValue.descripcion}));
                            }}
                            options={ubigeos}
                            autoHighlight
                            getOptionLabel={(option) => option.descripcion == null ? "" : option.descripcion}
                            isOptionEqualToValue={(option, value) => option.id === (value?.id ?? value)}
                            renderOption={(props, option) => {
                                return (
                                  <li
                                    {...props}
                                    key={option.id}
                                    onDoubleClick={() => {
                                        setSelection(option);
                                        openClose();
                                    }}
                                    sx={{ cursor: "pointer" }}
                                  >
                                    {option.descripcion}
                                  </li>
                                );
                              }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Ubigeo"
                                    variant="outlined"
                                    inputProps={{
                                        ...params.inputProps,
                                        autoComplete: 'new-password',
                                    }}
                                />
                            )}
                            size="small"
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ padding: "8px 24px 16px" }}>
                <Button color="primary" variant="contained" size="small" onClick={() => {
                    props.setSelection(searchUbigeo);
                    props.openClose();
                }}>Aceptar</Button>
                <Button size="small" onClick={() => { props.openClose(); }} >Cancelar</Button>
            </DialogActions>
        </Dialog>
    );
};

export default UbigeoSearch;
